import React, { useEffect, useState } from 'react';
import './MarketplaceCommsHub.scss';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import {
    getMarketplaceCommsHubTabs,
    MarketplaceCommsHubTab,
} from './MarketplaceCommsHubTabs';
import { Timeline } from '../../../../slices/marketplace/timeline/models/timeline.model';
import { MarketplaceBuyerCommunicationsTab } from './Tabs/MarketplaceBuyerCommunicationsTab/MarketplaceBuyerCommunicationsTab';
import { MarketplaceBrokerChatTab } from './Tabs/MarketplaceBrokerChatTab/MarketplaceBrokerChatTab';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { MarketplaceOpportunity } from '../../../../slices/marketplace/opportunity/models/opportunity';
import { useSearchParams } from 'react-router-dom';
import NoPageAccessAlert from '../../../../components/NoPageAccessAlert/NoPageAccessAlert';
import EngagementMeetings from '../../../../containers/ConnectMarketplaceDashboard/EngagementMeetings/EngagementMeetings';

interface MarketplaceCommsHubProps {
    engagement: Timeline;
    engagementId: number;
    meetingsGroupId?: number;
    setSearchParams?: (params: { [key: string]: string }) => void;
    notificationFlagPrefix: string;
    isMeetingTabAvailable: boolean;
}

export const MarketplaceCommsHub = (props: MarketplaceCommsHubProps) => {
    const [searchParams] = useSearchParams({});
    const marketplaceParty = useCurrentMarketplaceParty(props.engagement.id);
    const [activeTab, setActiveTab] = useState<MarketplaceCommsHubTab>();

    const getTimelineId = () => {
        if (marketplaceParty === TimelineParty.Buyer) {
            const selectedOpportunity =
                props.engagement as unknown as MarketplaceOpportunity;
            return selectedOpportunity.opportunityId;
        } else {
            return props.engagement.id;
        }
    };

    const tabComponents = [
        {
            id: MarketplaceCommsHubTab.BrokerChat,
            content: (
                <MarketplaceBrokerChatTab
                    party={marketplaceParty}
                    timeline={props.engagement}
                />
            ),
            notificationFlag: `${props.notificationFlagPrefix}.${props.engagement?.id}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.BrokerChatCode}`,
        },
        {
            id: MarketplaceCommsHubTab.BuyerCommunications,
            content: (
                <MarketplaceBuyerCommunicationsTab
                    party={marketplaceParty}
                    timeline={props.engagement}
                    notificationFlagPrefix={props.notificationFlagPrefix}
                />
            ),
            notificationFlag: `${
                props.notificationFlagPrefix
            }.${getTimelineId()}.${NotificationFlagsCategories.CommsHubCode}.${
                NotificationFlagsCategories.BuyerCommsCode
            }`,
        },
        {
            id: MarketplaceCommsHubTab.Meetings,
            content: (
                <EngagementMeetings
                    engagementId={props.engagementId}
                    meetingsGroupId={props.meetingsGroupId}
                    engagementBuyers={
                        props.engagement.engagementProcess?.buyers
                    }
                    isBrokerExist={props.engagement.engagementProcess?.broker?.isActive}
                    events={props.engagement.events}
                    engagementProcess={props.engagement.engagementProcess}
                />
            ),
            notificationFlag: `${props.notificationFlagPrefix}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingsCode}`,
        },
    ];

    useEffect(() => {
        const tabs = getMarketplaceCommsHubTabs(
            marketplaceParty,
            props.engagement.levelOfService,
            props.engagement.engagementProcess?.buyers?.some(
                (b) => b.isDataNdaSigned && b.isActive
            ),
            doesBrokerChatExist(),
            props.isMeetingTabAvailable
        );

        const value = searchParams.get('subtab');
        const numberValue = Number(value);

        if (
            value &&
            numberValue &&
            numberValue >= 1 &&
            numberValue <= tabComponents.length
        ) {
            const isTabAvailable = tabs.some((tab) => tab.id === numberValue);
            if (isTabAvailable) {
                setActiveTab(numberValue);
            } else if (tabs.length > 0) {
                setActiveTab(tabs[0]?.id);
            } else {
                setActiveTab(undefined);
            }
        } else {
            if (tabs.length > 0) {
                setActiveTab(tabs[0]?.id);
            } else {
                setActiveTab(undefined);
            }
        }

        if (numberValue > tabComponents.length) {
            props.setSearchParams?.({
                subtab: '',
            });
        }
    }, [searchParams]);

    const doesBrokerChatExist = () => {
        const broker = props.engagement?.engagementProcess?.broker;
        return !!(broker && broker.isActive && broker.brokerChatId);
    };

    const renderTabContent = () => {
        if (!activeTab) {
            return null;
        }
        const activeTabComponent = tabComponents.find(
            (tab) => tab.id === activeTab
        );
        const isBuyerChatsEnabled =
            props.engagement.engagementProcess?.isBuyerChatsEnabled ||
            (props.engagement as unknown as MarketplaceOpportunity)
                .isBuyerChatsEnabled;
        return activeTabComponent?.id ===
            MarketplaceCommsHubTab.BuyerCommunications &&
            !isBuyerChatsEnabled &&
            marketplaceParty !== TimelineParty.VetValue ? (
            <div className="mt-4">
                <NoPageAccessAlert text={'Chat is deactivated'} />
            </div>
        ) : (
            activeTabComponent?.content || (
                <div className="d-flex align-items-center justify-content-center mt-4">
                    <h1>Empty Tab</h1>
                </div>
            )
        );
    };

    const getTabNotificationFlag = (tab: number) => {
        const targetCode = tabComponents.find((t) => t.id === tab);
        return targetCode ? (
            <>
                <NotificationFlag
                    flagClassName="notification-flag-wrapper"
                    eventCode={targetCode.notificationFlag}
                    includePrefix={true}></NotificationFlag>
            </>
        ) : (
            <></>
        );
    };

    return (
        <div className="marketplace-comms-hub marketplace-dynamic-container">
            <div className="RRT__container">
                <div className="RRT__tabs body-tabs">
                    {getMarketplaceCommsHubTabs(
                        marketplaceParty,
                        props.engagement.levelOfService,
                        props.engagement.engagementProcess?.buyers?.some(
                            (b) => b.isDataNdaSigned && b.isActive
                        ) ?? false,
                        !!props.engagement?.engagementProcess?.broker
                            ?.brokerChatId &&
                            props.engagement.engagementProcess.broker.isActive,
                        props.isMeetingTabAvailable
                    )
                        .filter(
                            (tab) =>
                                !(
                                    tab.id ===
                                        MarketplaceCommsHubTab.BuyerCommunications &&
                                    !(
                                        props.engagement.engagementProcess
                                            ?.isBuyerChatsEnabled ||
                                        (
                                            props.engagement as unknown as MarketplaceOpportunity
                                        ).isBuyerChatsEnabled
                                    ) &&
                                    marketplaceParty !== TimelineParty.VetValue
                                )
                        )
                        .map((item) => {
                            return (
                                <div
                                    key={item.id}
                                    className={
                                        activeTab === item.id
                                            ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                            : 'RRT__tab'
                                    }
                                    onClick={() => {
                                        setActiveTab(item.id);
                                        props.setSearchParams?.({
                                            subtab: item.id.toString(),
                                        });
                                    }}>
                                    {item.content}
                                    {getTabNotificationFlag(item.id)}
                                </div>
                            );
                        })}
                </div>
            </div>
            {renderTabContent()}
        </div>
    );
};
